import { formatQuantity, isGreater, snapQuantity } from '@gfe/core/utils/quantity-utils';
import { compareAlphabetically, dedup, isNil } from '@gfe/core/utils/shared-utils';
import {
  Duration,
  addDurationFromIso,
  addToDuration,
  formatDurationFromIso,
  parseISO8601Duration,
  toISO8601Duration
} from '@gfe/core/utils/time-utils';
import { CalendarEvent } from '@gfe/features/components/visualization/calendar/models/calendar.models';
import { VisuItemLabel } from '@gfe/features/components/visualization/common/models/label.model';
import { GaliaArea } from '@gfe/features/components/visualization/mapbox/models/galia-area';
import {
  GaliaMarker,
  MarkerType
} from '@gfe/features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@gfe/features/components/visualization/mapbox/models/galia-route';
import { TaskKind } from '@gfe/features/components/visualization/timeline/models/timeline';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import { VisualizationBuilders } from '@core/models/entities/generic-entity';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { EntityLabelsTranslationService } from '@core/services/entity-labels-translation/entity-labels-translation.service';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { FilterDataType } from '@shared/components/filter-query-builder/filter.models';

import { routeColor } from '../../colors.model';
import {
  AuxiliaryDataBundle,
  AuxiliaryDataFetchDetails,
  CalendarVisuInterface,
  EntityCfg,
  EntityInterface,
  MapVisuInterface,
  TableColCfg
} from '../entity-visualization-interface';
import { SplitEntityRow } from '../split-columns.model';
import { Coords } from './coords';
import { CustomData } from './custom-data';
import { measureUnitSymbol } from './measure-unit';
import {
  UserHolding,
  UserMove,
  UserMovement,
  UserPlacement,
  composeHoldingLabelValues,
  computeTemperatureSegments,
  countsByOccupantAndPlacement,
  extractCompartmentUids,
  flattenHoldings,
  flattenMoves,
  foldTemperatureState,
  getCompartmentPlacementKey,
  hasCompartmentPlacements,
  hasMultipleCompartmentCopies,
  heldTemperatureUids,
  isCompartmentPlacement,
  placementKeyIn
} from './route-action-occupants';
import { UserSiteV1Model } from './user-site-v1';

export {
  computeTemperatureSegments,
  extractCompartmentUids,
  flattenHoldings,
  flattenMoves,
  foldTemperatureState,
  getCompartmentPlacementKey,
  hasMultipleCompartmentCopies,
  heldTemperatureUids,
  isCompartmentPlacement
};
export type { UserHolding, UserMove, UserMovement };

export type RouteActionType = 'basic' | 'pickup' | 'delivery' | 'delivery-pickup';

/**
 * Optional marker set by the solver on an action whose service does not map to a
 * simple sequence of per-job load/unload operations (e.g. preloading, trailer
 * exchange).
 */
export type RouteActionModifier = 'PreLoading' | 'TrailerExchange';

type RouteActionStepKind =
  | 'Drive'
  | 'Wait'
  | 'WaitDuringService'
  | 'InsideSetup'
  | 'OutsideSetup'
  | 'InsideSetdown'
  | 'OutsideSetdown'
  | 'Pause'
  | 'PauseDuringService'
  | 'HomeRest'
  | 'ActiveService';

export function routeActionStepKindToTaskKind(kind: RouteActionStepKind): TaskKind {
  switch (kind) {
    case 'Drive':
      return 'drive';
    case 'Wait':
    case 'WaitDuringService':
      return 'wait';
    case 'InsideSetup':
    case 'OutsideSetup':
      return 'setup';
    case 'InsideSetdown':
    case 'OutsideSetdown':
      return 'setdown';
    case 'Pause':
    case 'PauseDuringService':
      return 'pause';
    // Kept apart from 'pause': a home rest is the only observable marker of a duty
    // boundary, and folding it into a pause erases the route's duty structure.
    case 'HomeRest':
      return 'home-rest';
    case 'ActiveService':
      return 'work';
    default:
      throw new Error(`Unhandled RouteActionStepKind: ${kind}`);
  }
}

export interface RouteActionStep {
  kind: RouteActionStepKind;
  duration: string;
}

/**
 * One interval during which a stopover held a quantity of a site's infrastructure.
 * Half-open: [start, end). Produced by DL; see the DL contract doc.
 */
export interface UserInfrastructureUsage {
  infrastructure_uid: string;
  quantity: number;
  /** RFC3339 with offset, inclusive. */
  start: string;
  /** RFC3339 with offset, exclusive. */
  end: string;
}

export interface UserRouteActionV1Model {
  uid: string;
  site_uid: string;
  coords?: Coords;
  route_uid: string;
  seq_nb: number;
  site_visit_nb: number;
  movements: UserMovement[];
  steps_from_previous_finish: RouteActionStep[];
  travel_meters_from_previous_finish: number;
  scheduled_first_step_start_datetime: string;
  holdings: UserHolding[];
  /** Absent on plans produced before the field existed. */
  infrastructure_usages?: UserInfrastructureUsage[];
  polyline?: string;
  modifier?: RouteActionModifier;
  custom_data: CustomData;
}

/**
 * Authoritative list of job uids handled at an action, deduped. No-load "service" jobs surface as
 * service-only movements (empty `moved`) that still carry their `job_uid`.
 */
export function getHandledJobUids(action: UserRouteActionV1Model): string[] {
  // The backend schema makes `movements` optional with a default of `[]`, so it can
  // arrive as `undefined` despite the non-optional type here.
  return dedup(
    (action.movements ?? [])
      .map((m) => m.job_uid)
      .filter((uid): uid is string => !isNil(uid))
  );
}

/**
 * First-class operation entities handled at an action, grouped by `operation_entity_type`
 * (e.g. `JOB_WASTE_CONTAINER_STEP`). Empty when the action carries no handled operations.
 */
export function getHandledOperationsByType(
  action: UserRouteActionV1Model
): Record<string, string[]> {
  const byType: Record<string, string[]> = {};
  for (const movement of action.movements) {
    if (isNil(movement.operation_uid) || isNil(movement.operation_entity_type)) {
      continue;
    }
    if (!byType[movement.operation_entity_type]) {
      byType[movement.operation_entity_type] = [];
    }
    if (!byType[movement.operation_entity_type].includes(movement.operation_uid)) {
      byType[movement.operation_entity_type].push(movement.operation_uid);
    }
  }
  return byType;
}

function makeJobOperationLabel(job: [string, string], showKinds: boolean = false): string {
  if (showKinds) {
    return job[0] + ' (' + job[1] + ')';
  } else {
    return job[0];
  }
}

function makeJobsLabel(action: UserRouteActionV1Model): string {
  const operationKindsByJob: Record<string, Set<string>> = {};
  for (const movement of action.movements) {
    if (isNil(movement.job_uid)) continue;
    if (!operationKindsByJob[movement.job_uid]) {
      operationKindsByJob[movement.job_uid] = new Set<string>();
    }
    if (!isNil(movement.operation_kind)) {
      operationKindsByJob[movement.job_uid].add(movement.operation_kind);
    }
  }

  const allKinds = new Set<string>();
  Object.values(operationKindsByJob).forEach((kinds) =>
    kinds.forEach((k) => allKinds.add(k))
  );
  const hasManyOperationKinds = allKinds.size > 1;

  return getHandledJobUids(action)
    .map((jobUid) => {
      const operationKinds = Array.from(operationKindsByJob[jobUid] ?? []);
      return makeJobOperationLabel(
        [jobUid, operationKinds.join(', ')],
        hasManyOperationKinds
      );
    })
    .join(', ');
}

function makeActionKindsLabel(action: UserRouteActionV1Model): string {
  const uniqueKinds = new Set<string>();
  for (const movement of action.movements) {
    if (!isNil(movement.operation_kind)) {
      uniqueKinds.add(movement.operation_kind);
    }
  }
  return Array.from(uniqueKinds).join(', ');
}

function makeTravelKmLabel(action: UserRouteActionV1Model): string {
  return '' + (action.travel_meters_from_previous_finish / 1000).toFixed(1) + ' km';
}

function computeTravelDuration(action: UserRouteActionV1Model): Duration {
  let dur = parseISO8601Duration('');

  action.steps_from_previous_finish.forEach((step: RouteActionStep) => {
    if (step.kind == 'Drive') {
      dur = addToDuration(dur, step.duration);
    }
  });

  return dur;
}

function computeTotalServiceDuration(action: UserRouteActionV1Model): Duration {
  let dur = parseISO8601Duration('');

  action.steps_from_previous_finish.forEach((step: RouteActionStep) => {
    if (step.kind == 'ActiveService') {
      dur = addToDuration(dur, step.duration);
    }
  });

  return dur;
}

function computeTotalSetupDuration(action: UserRouteActionV1Model): Duration {
  let dur = parseISO8601Duration('');

  action.steps_from_previous_finish.forEach((step: RouteActionStep) => {
    if (step.kind == 'InsideSetup' || step.kind == 'OutsideSetup') {
      dur = addToDuration(dur, step.duration);
    }
  });

  return dur;
}

function computeTotalSetdownDuration(action: UserRouteActionV1Model): Duration {
  let dur = parseISO8601Duration('');

  action.steps_from_previous_finish.forEach((step: RouteActionStep) => {
    if (step.kind == 'InsideSetdown' || step.kind == 'OutsideSetdown') {
      dur = addToDuration(dur, step.duration);
    }
  });

  return dur;
}

export function formatCapacityValue(value: number): string {
  return formatQuantity(value);
}

/**
 * Converts compartment quantities map to display format
 * @param quantities Map of compartment -> { capacity_uid -> quantity }
 * @returns Map of compartment -> formatted string (e.g., "100 kg, 50 liters")
 */
export function formatCompartmentQuantities(
  quantities: Record<string, Record<string, number>>
): Record<string, string> {
  const formatted: Record<string, string> = {};

  Object.entries(quantities).forEach(([compartment, capacities]) => {
    const parts = Object.entries(capacities)
      .filter(([, quantity]) => isGreater(quantity, 0))
      .sort(([capacityUidA], [capacityUidB]) =>
        compareAlphabetically(capacityUidA, capacityUidB)
      )
      .map(([capacityUid, quantity]) => `${formatCapacityValue(quantity)} ${capacityUid}`);

    if (parts.length > 0) {
      formatted[compartment] = parts.join(', ');
    }
  });

  return formatted;
}

/** Sums capacity-axis moves of one direction, keyed by placement (compartment-aware). */
function capacityByPlacement(
  action: UserRouteActionV1Model,
  direction: 'load' | 'unload'
): Record<string, string> {
  const flat = flattenMoves(action.movements);
  const allPlacements = flat
    .map((f) => f.placement)
    .filter((p): p is UserPlacement => !isNil(p));

  const quantities: Record<string, Record<string, number>> = {};
  flat
    .filter(
      (f) =>
        f.kind === direction &&
        f.axis === 'capacity' &&
        !isNil(f.capacity_resource_uid) &&
        !isNil(f.placement) &&
        isGreater(f.quantity, 0)
    )
    .forEach((f) => {
      const key = placementKeyIn(allPlacements, f.placement as UserPlacement);
      const cap = f.capacity_resource_uid as string;
      if (!quantities[key]) {
        quantities[key] = {};
      }
      quantities[key][cap] = (quantities[key][cap] ?? 0) + f.quantity;
    });

  return formatCompartmentQuantities(quantities);
}

export function computeTotalLoaded(action: UserRouteActionV1Model): Record<string, string> {
  return capacityByPlacement(action, 'load');
}

export function computeTotalUnloaded(
  action: UserRouteActionV1Model
): Record<string, string> {
  return capacityByPlacement(action, 'unload');
}

/** Sums capacity-axis moves of one direction, keyed by job (compartment-qualified when present). */
function capacityByJobAndPlacement(
  action: UserRouteActionV1Model,
  direction: 'load' | 'unload'
): Record<string, string> {
  const flat = flattenMoves(action.movements);
  const allPlacements = flat
    .map((f) => f.placement)
    .filter((p): p is UserPlacement => !isNil(p));

  const quantities: Record<string, Record<string, number>> = {};
  flat
    .filter(
      (f) =>
        f.kind === direction &&
        f.axis === 'capacity' &&
        !isNil(f.capacity_resource_uid) &&
        f.quantity > 0.0
    )
    .forEach((f) => {
      const placement = f.placement;
      const compartmentKey =
        !isNil(placement) && isCompartmentPlacement(placement)
          ? placementKeyIn(allPlacements, placement)
          : undefined;
      const jobUid = f.job_uid ?? '';
      const key = !isNil(compartmentKey) ? `${jobUid}: ${compartmentKey}` : jobUid;
      const cap = f.capacity_resource_uid as string;
      if (!quantities[key]) {
        quantities[key] = {};
      }
      quantities[key][cap] = (quantities[key][cap] ?? 0) + f.quantity;
    });

  return formatCompartmentQuantities(quantities);
}

/**
 * An action splits its leg into several drive steps around pauses and waits, but the solver
 * only reports the distance of the whole leg, so that is what every drive bubble shows.
 */
export function createDriveTaskLabels(
  step: RouteActionStep,
  action: UserRouteActionV1Model,
  temperaturesForTravel: string[],
  holdingsAboard: UserHolding[]
): VisuItemLabel[] {
  const labels: VisuItemLabel[] = [];
  labels.push({
    label: 'leg_travel_distance',
    level: 0,
    value: makeTravelKmLabel(action)
  });

  labels.push({
    label: 'travel_duration',
    level: 0,
    value: formatDurationFromIso(step.duration)
  });

  if (temperaturesForTravel.length > 0) {
    labels.push({
      label: 'operating_temperatures',
      level: 0,
      value: temperaturesForTravel.join(', ')
    });
  }

  composeHoldingLabelValues(holdingsAboard).forEach((value) => {
    labels.push({ label: 'cargo_aboard', level: 0, value });
  });

  return labels;
}

export function createStepDurationLabels(step: RouteActionStep): VisuItemLabel[] {
  return [{ label: 'duration', level: 0, value: formatDurationFromIso(step.duration) }];
}

export function createActionLabels(
  action: UserRouteActionV1Model,
  routeUid: string,
  vehicleUid?: string,
  showKinds: boolean = false
): VisuItemLabel[] {
  const labels: VisuItemLabel[] = [{ label: 'site', level: 0, value: action.site_uid }];

  const useCompartmentLabels = hasCompartmentPlacements(action.movements);

  const totalLoaded = computeTotalLoaded(action);
  Object.entries(totalLoaded).forEach(([compartment, value]) => {
    labels.push({
      label: 'total_loaded',
      level: 0,
      value: useCompartmentLabels ? `${value} (${compartment})` : value
    });
  });

  const totalUnloaded = computeTotalUnloaded(action);
  Object.entries(totalUnloaded).forEach(([compartment, value]) => {
    labels.push({
      label: 'total_unloaded',
      level: 0,
      value: useCompartmentLabels ? `${value} (${compartment})` : value
    });
  });

  labels.push({
    label: 'seq_nb',
    level: 1,
    value: action.seq_nb.toString()
  });
  labels.push({
    label: 'site_visit_nb',
    level: 1,
    value: action.site_visit_nb.toString()
  });

  const temperatureUids = heldTemperatureUids(action.holdings);
  const temperatureUidsString =
    temperatureUids.length > 0 ? temperatureUids.join(', ') : undefined;

  if (!isNil(temperatureUidsString)) {
    labels.push({
      label: 'operating_temperatures',
      level: 1,
      value: temperatureUidsString
    });
  }

  // Add compartment labels
  const compartmentUids = extractCompartmentUids(action.movements);
  if (compartmentUids.length > 0) {
    labels.push({
      label: 'compartments',
      level: 1,
      value: compartmentUids.join(', ')
    });
  }

  labels.push({ label: 'route', level: 1, value: routeUid });
  if (!isNil(vehicleUid)) {
    labels.push({ label: 'vehicle', level: 1, value: vehicleUid });
  }

  const jobsLabelValue = makeJobsLabel(action);
  if (jobsLabelValue !== '') {
    labels.push({ label: 'jobs', level: 1, value: jobsLabelValue });
  }

  const operationUids = Object.values(getHandledOperationsByType(action)).flat();
  if (operationUids.length > 0) {
    labels.push({ label: 'operations', level: 1, value: operationUids.join(', ') });
  }
  if (showKinds) {
    const kindsLabelValue = makeActionKindsLabel(action);
    if (kindsLabelValue !== '') {
      labels.push({ label: 'kinds', level: 1, value: kindsLabelValue });
    }
  }

  composeHoldingLabelValues(action.holdings).forEach((value) => {
    labels.push({ label: 'load_state', level: 1, value });
  });

  return labels;
}

export function getFirstServiceStartOrScheduledFirstStepStart(
  action: UserRouteActionV1Model
): Date {
  let curDate = new Date(action.scheduled_first_step_start_datetime);
  for (const step of action.steps_from_previous_finish) {
    if (step.kind == 'ActiveService') {
      return curDate;
    }
    curDate = addDurationFromIso(curDate, step.duration);
  }
  return curDate;
}

export function getLastServiceFinish(action: UserRouteActionV1Model): Date | undefined {
  let curDate = new Date(action.scheduled_first_step_start_datetime);
  let lastServiceFinish = undefined;
  for (const step of action.steps_from_previous_finish) {
    curDate = addDurationFromIso(curDate, step.duration);
    if (step.kind == 'ActiveService') {
      lastServiceFinish = curDate;
    }
  }
  return lastServiceFinish;
}

/**
 * A stopover's map coordinates come from the auxiliary `UserSite` fetch rather than from the
 * stopover row, so an unresolved site drops the marker. Say so: the failure mode is otherwise
 * an empty map with nothing reported anywhere.
 */
export function warnUnresolvedStopoverSites(context: string, siteUids: string[]): void {
  if (siteUids.length === 0) {
    return;
  }
  console.warn(
    `[RouteAction] ${context}: no auxiliary UserSite resolved for site uid(s) ` +
      `${dedup(siteUids).join(', ')}; those stopovers are dropped from the map.`
  );
}

export function getRouteActionType(action: UserRouteActionV1Model): RouteActionType {
  const flat = flattenMoves(action.movements);
  const hasPickup = flat.some((f) => f.kind === 'load' && f.quantity >= 1e-10);
  const hasDelivery = flat.some((f) => f.kind === 'unload' && f.quantity >= 1e-10);

  if (hasPickup && hasDelivery) {
    return 'delivery-pickup';
  } else if (hasPickup) {
    return 'pickup';
  } else if (hasDelivery) {
    return 'delivery';
  } else {
    return 'basic';
  }
}

/**
 * `seq_nb` numbers a route's actions from 1, so the sequence extremes are the route's start and
 * finish. `actionCount` is unknown when a stopover is visualized on its own and the route behind
 * it could not be resolved; nothing then distinguishes the last stopover from any other, so it
 * falls back to its movement-derived type.
 */
export function getStopoverMarkerType(
  action: UserRouteActionV1Model,
  actionCount?: number
): MarkerType {
  if (action.seq_nb === 1) {
    return 'start';
  }
  if (!isNil(actionCount) && action.seq_nb === actionCount) {
    return 'finish';
  }
  return getRouteActionType(action);
}

/**
 * Like {@link getRouteActionType} but restricted to a single job's movements
 * within the action. Used to color a job's timeline block by whether, at this
 * stop, the job is picked up, delivered, or both.
 */
export function getJobActionType(
  action: UserRouteActionV1Model,
  jobUid: string
): RouteActionType {
  const jobMoves = flattenMoves(action.movements).filter((f) => f.job_uid === jobUid);
  const hasPickup = jobMoves.some((f) => f.kind === 'load' && f.quantity >= 1e-10);
  const hasDelivery = jobMoves.some((f) => f.kind === 'unload' && f.quantity >= 1e-10);

  if (hasPickup && hasDelivery) {
    return 'delivery-pickup';
  } else if (hasPickup) {
    return 'pickup';
  } else if (hasDelivery) {
    return 'delivery';
  } else {
    return 'basic';
  }
}

/**
 * Whether an action services the given job, i.e. it carries at least one movement
 * with this `job_uid` — including no-load service movements (empty `moved`), so
 * such stops still appear on the job timeline.
 */
export function actionHandlesJob(action: UserRouteActionV1Model, jobUid: string): boolean {
  return action.movements.some((m) => m.job_uid === jobUid);
}

export interface ServiceWindow {
  start: Date;
  finish: Date;
}

/**
 * Computes the scheduled service window of a single job inside a route action, from
 * the `service_start`/`service_end` of that job's own movements (spanning the first
 * start to the last finish when the job has more than one movement in the action).
 *
 * The whole action service window is used instead when the job's own sub-window
 * collapses to zero width (fallback, so the block stays visible).
 *
 * Returns `undefined` when the action does not touch the job.
 */
export function computeJobServiceWindowInAction(
  action: UserRouteActionV1Model,
  jobUid: string
): ServiceWindow | undefined {
  if (!actionHandlesJob(action, jobUid)) {
    return undefined;
  }

  const serviceStart = getFirstServiceStartOrScheduledFirstStepStart(action);
  const wholeActionWindow = (): ServiceWindow => ({
    start: serviceStart,
    finish: getLastServiceFinish(action) ?? serviceStart
  });

  const jobMovements = action.movements.filter((m) => m.job_uid === jobUid);
  const starts = jobMovements.map((m) => new Date(m.service_start).getTime());
  const ends = jobMovements.map((m) => new Date(m.service_end).getTime());
  const jobStart = new Date(Math.min(...starts));
  const jobFinish = new Date(Math.max(...ends));

  if (jobFinish.getTime() <= jobStart.getTime()) {
    return wholeActionWindow();
  }
  return { start: jobStart, finish: jobFinish };
}

type RawModel = UserRouteActionV1Model;

type CapacityGroup = 'total_unloaded' | 'total_loaded' | 'load_state';

interface CapacityEntry {
  placement: UserPlacement;
  capacity_resource_uid: string;
  quantity: number;
}

/** Flattens an action into {placement, capacity, quantity} rows for one group. */
function capacityEntriesForGroup(
  action: UserRouteActionV1Model,
  group: CapacityGroup
): CapacityEntry[] {
  if (group === 'load_state') {
    return flattenHoldings(action.holdings)
      .filter(
        (holding) => holding.axis === 'capacity' && !isNil(holding.capacity_resource_uid)
      )
      .map((holding) => ({
        placement: holding.placement,
        capacity_resource_uid: holding.capacity_resource_uid as string,
        quantity: holding.quantity as number
      }));
  }
  const direction = group === 'total_unloaded' ? 'unload' : 'load';
  return flattenMoves(action.movements)
    .filter(
      (f) =>
        f.kind === direction &&
        f.axis === 'capacity' &&
        !isNil(f.capacity_resource_uid) &&
        !isNil(f.placement)
    )
    .map((f) => ({
      placement: f.placement as UserPlacement,
      capacity_resource_uid: f.capacity_resource_uid as string,
      quantity: f.quantity
    }));
}

/** Sums a single capacity resource across an action (all placements/compartments). */
function sumCapacity(entries: CapacityEntry[], capacityUid: string): number {
  return entries
    .filter((e) => e.capacity_resource_uid === capacityUid && isGreater(e.quantity, 0))
    .reduce((total, e) => total + e.quantity, 0);
}

/**
 * Builds one numeric column per capacity resource for a capacity group, each cell
 * showing the plain summed value for that resource. Returns the original combined
 * column as a single-element array when no capacity resources have been fetched yet,
 * so the table degrades gracefully.
 */
function buildCapacityColumns(
  group: CapacityGroup,
  capacityResources: SplitEntityRow[]
): TableColCfg<EntityInterface<RawModel>>[] {
  if (capacityResources.length === 0) {
    return [
      {
        key: `raw.${group}` as keyof EntityInterface<RawModel>,
        label: `table.columns.${group}`,
        type: 'json'
      }
    ];
  }
  const prefix = EntityLabelsTranslationService.translateLabelStatic(
    `table.columns.${group}`
  );
  return capacityResources.map((cap) => {
    const capName = cap.name ?? cap.uid;
    return {
      key: `raw.${group}.${cap.uid}` as keyof EntityInterface<RawModel>,
      label: `${prefix} ${capName}`,
      staticLabel: true,
      type: 'number',
      unit: measureUnitSymbol(cap.unit),
      summaryTypes: ['sum', 'avg', 'max', 'min'],
      rawValue: (row?: EntityInterface<RawModel>): unknown => {
        const action = row?.raw;
        if (isNil(action)) return 0;
        return sumCapacity(capacityEntriesForGroup(action, group), cap.uid);
      },
      renderHook: (_value: unknown, row?: EntityInterface<RawModel>): unknown => {
        const action = row?.raw;
        if (isNil(action)) return null;
        const total = sumCapacity(capacityEntriesForGroup(action, group), cap.uid);
        return isGreater(total, 0) ? snapQuantity(total) : null;
      }
    };
  });
}

export class UserRouteActionCfg implements EntityCfg<RawModel> {
  static readonly visus = new Set<VisualizationModule>(['map', 'calendar']);

  supportedVisualizations(): Set<VisualizationModule> {
    return UserRouteActionCfg.visus;
  }

  versionName(): string {
    return 'UserRouteAction';
  }

  defaultSortingCol(): string[] {
    return ['route_uid', 'seq_nb'];
  }

  availableDetailEntityType(): string[] {
    return [];
  }

  makeAuxDataFetchDetails(_entities: RawModel[]): AuxiliaryDataFetchDetails {
    return { versionNameAndUidsPerType: {} };
  }

  buildColumns(
    getSplitEntities?: (targetVersionName: string) => SplitEntityRow[]
  ): TableColCfg<EntityInterface<RawModel>>[] {
    // Capacity resources are declared as x-split-columns on the UserRouteAction
    // schema and fetched by SplitColumnsService; resolve them by target versionName.
    const capacityResources = getSplitEntities
      ? getSplitEntities('UserCapacityResource')
      : [];
    const cols: TableColCfg<EntityInterface<RawModel>>[] = [
      {
        key: 'raw.uid' as keyof EntityInterface<RawModel>,
        label: 'table.columns.uid',
        type: 'string',
        sortCfg: { dbColName: 'uid' },
        filterCfg: {
          translationLabel: 'uid',
          dbColName: 'uid',
          filterType: FilterDataType.TEXT
        }
      },
      {
        key: 'raw.route_uid' as keyof EntityInterface<RawModel>,
        label: 'table.columns.route',
        type: 'uid',
        sortCfg: { dbColName: 'route_uid' },
        filterCfg: {
          translationLabel: 'route',
          dbColName: 'route_uid',
          filterType: FilterDataType.UID,
          uidEntityType: 'ROUTE'
        }
      },
      {
        key: 'raw.site_uid' as keyof EntityInterface<RawModel>,
        label: 'table.columns.site',
        type: 'uid',
        sortCfg: { dbColName: 'site_uid' },
        filterCfg: {
          translationLabel: 'site',
          dbColName: 'site_uid',
          filterType: FilterDataType.UID,
          uidEntityType: 'SITE'
        }
      },
      {
        key: 'raw.seq_nb' as keyof EntityInterface<RawModel>,
        label: 'table.columns.seq_nb',
        type: 'number',
        sortCfg: { dbColName: 'seq_nb' }
      },
      {
        key: 'raw.site_visit_nb' as keyof EntityInterface<RawModel>,
        label: 'table.columns.site_visit_nb',
        type: 'number',
        sortCfg: { dbColName: 'site_visit_nb' }
      },
      {
        key: 'raw.temperatureUids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.temperatures',
        type: 'uid-list',
        referenceEntityType: 'TEMPERATURE',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }

          return heldTemperatureUids(action.holdings);
        }
      },
      {
        key: 'raw.commodityUids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.commodity_uids',
        type: 'uid-list',
        cellTemplate: 'default',
        referenceEntityType: 'COMMODITY',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }

          return dedup(
            flattenMoves(action.movements)
              .map((f) => f.commodity_uid)
              .filter((commodityUid) => !isNil(commodityUid))
          );
        }
      },
      {
        key: 'raw.productUids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.product_uids',
        type: 'uid-list',
        cellTemplate: 'default',
        referenceEntityType: 'PRODUCT',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }

          return dedup(
            flattenMoves(action.movements)
              .map((f) => f.product_uid)
              .filter((productUid) => !isNil(productUid))
          );
        }
      },
      {
        key: 'raw.onboardCommodityUids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.onboard_commodity_uids',
        type: 'uid-list',
        cellTemplate: 'default',
        referenceEntityType: 'COMMODITY',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }

          return dedup(
            flattenHoldings(action.holdings)
              .map((holding) => holding.commodity_uid)
              .filter((commodityUid) => !isNil(commodityUid))
          );
        }
      },
      {
        key: 'raw.onboardProductUids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.onboard_product_uids',
        type: 'uid-list',
        cellTemplate: 'default',
        referenceEntityType: 'PRODUCT',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }

          return dedup(
            flattenHoldings(action.holdings)
              .map((holding) => holding.product_uid)
              .filter((productUid) => !isNil(productUid))
          );
        }
      },
      {
        key: 'raw.onboardCounts' as keyof EntityInterface<RawModel>,
        label: 'table.columns.onboard_counts',
        type: 'string-number-map',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }

          return countsByOccupantAndPlacement(action.holdings);
        }
      },
      {
        key: 'raw.job_uids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.job_uids',
        type: 'uid-list',
        referenceEntityType: 'JOB',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return [];
          }
          return getHandledJobUids(action);
        }
      },
      {
        // A single static referenceEntityType is valid while JOB_WASTE_CONTAINER_STEP is the only
        // operation entity type; a mix of types would need per-cell reference typing.
        key: 'raw.operations' as keyof EntityInterface<RawModel>,
        label: 'table.columns.operations',
        type: 'uid-list',
        referenceEntityType: 'JOB_WASTE_CONTAINER_STEP',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return [];
          }
          return Object.values(getHandledOperationsByType(action)).flat();
        }
      },
      {
        key: 'raw.start' as keyof EntityInterface<RawModel>,
        label: 'table.columns.scheduled_window',
        type: 'datetime-window',
        cellTemplate: 'default',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }
          const start = getFirstServiceStartOrScheduledFirstStepStart(
            action as UserRouteActionV1Model
          );
          const finish = getLastServiceFinish(action as UserRouteActionV1Model) ?? start;

          return {
            start,
            finish
          };
        },
        sortCfg: { dbColName: 'scheduled_first_step_start_time' }
      },
      ...buildCapacityColumns('total_loaded', capacityResources),
      ...buildCapacityColumns('total_unloaded', capacityResources),
      ...buildCapacityColumns('load_state', capacityResources),
      {
        key: 'raw.serviceDuration' as keyof EntityInterface<RawModel>,
        label: 'table.columns.service_duration',
        type: 'duration',
        cellTemplate: 'default',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }
          const dur = computeTotalServiceDuration(action as UserRouteActionV1Model);
          return toISO8601Duration(dur);
        }
      },
      {
        key: 'raw.travel_meters_from_previous_finish' as keyof EntityInterface<RawModel>,
        label: 'table.columns.travel_km',
        type: 'number',
        cellTemplate: 'default',
        renderHook(meters) {
          return `${((meters as number) / 1000).toFixed(1)} km`;
        }
      },
      {
        key: 'raw.travelDuration' as keyof EntityInterface<RawModel>,
        label: 'table.columns.travel_duration',
        type: 'duration',
        cellTemplate: 'default',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }
          const dur = computeTravelDuration(action as UserRouteActionV1Model);
          return toISO8601Duration(dur);
        }
      },
      {
        key: 'raw.setup' as keyof EntityInterface<RawModel>,
        label: 'table.columns.total_setup',
        type: 'duration',
        cellTemplate: 'default',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }
          const dur = computeTotalSetupDuration(action as UserRouteActionV1Model);
          return toISO8601Duration(dur);
        }
      },
      {
        key: 'raw.setdown' as keyof EntityInterface<RawModel>,
        label: 'table.columns.total_setdown',
        type: 'duration',
        cellTemplate: 'default',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }
          const dur = computeTotalSetdownDuration(action as UserRouteActionV1Model);
          return toISO8601Duration(dur);
        }
      },
      {
        key: 'raw.load' as keyof EntityInterface<RawModel>,
        label: 'table.columns.load',
        type: 'string-number-map',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }
          return capacityByJobAndPlacement(action, 'load');
        }
      },
      {
        key: 'raw.unload' as keyof EntityInterface<RawModel>,
        label: 'table.columns.unload',
        type: 'string-number-map',
        renderHook(_value, row) {
          const action = row?.raw;
          if (isNil(action)) {
            return '';
          }
          return capacityByJobAndPlacement(action, 'unload');
        }
      },
      {
        key: 'raw.custom_data.tags' as keyof EntityInterface<RawModel>,
        label: 'table.columns.tags',
        type: 'uid-list',
        filterCfg: {
          translationLabel: 'tags',
          dbColName: 'tags',
          filterType: FilterDataType.TAGS
        }
      },
      {
        key: 'raw.custom_data.labels' as keyof EntityInterface<RawModel>,
        label: 'table.columns.custom_labels',
        type: 'string-number-map',
        filterCfg: {
          translationLabel: 'custom_labels',
          dbColName: 'labels',
          filterType: FilterDataType.LABELS
        }
      }
    ];

    return cols;
  }
}

/** The color of the route an action belongs to, read from the route when it was loaded. */
function colorOfRoute(routeUid: string, auxData?: AuxiliaryDataBundle): string {
  const route = (
    (auxData?.entitiesArrayPerVersionName['UserRoute'] ?? []) as {
      uid: string;
      color?: string | null;
    }[]
  ).find((candidate) => candidate.uid === routeUid);
  return routeColor(routeUid, route?.color);
}

export class UserRouteActionV1
  implements EntityInterface<RawModel>, MapVisuInterface, CalendarVisuInterface
{
  private readonly color: string = '#4e4c4c';
  private readonly auxiliarySites: Record<string, UserSiteV1Model>;

  constructor(
    public raw: RawModel,
    auxData: AuxiliaryDataBundle = { entitiesArrayPerVersionName: {} }
  ) {
    this.color = colorOfRoute(this.raw.route_uid, auxData);
    const auxSites = (auxData.entitiesArrayPerVersionName['UserSite'] ??
      []) as UserSiteV1Model[];
    this.auxiliarySites = Object.fromEntries(auxSites.map((site) => [site.uid, site]));
  }

  supportedVisualizations(): Set<VisualizationModule> {
    return UserRouteActionCfg.visus;
  }

  get uid(): string {
    return this.raw.uid;
  }

  buildMarkers(): GaliaMarker[] {
    const action = this.raw;
    const labels = createActionLabels(this.raw, this.raw.uid);
    const coords = this.auxiliarySites[action.site_uid]?.coords;

    if (isNil(coords)) {
      return [];
    }

    return [
      {
        uid: action.uid,
        title: action.uid,
        coords,
        color: this.color,
        type: getRouteActionType(action),
        description: {
          name: action.uid,
          startDate: getFirstServiceStartOrScheduledFirstStepStart(action),
          endDate: getLastServiceFinish(action),
          content: labels
        },
        structuralAggregatorIds: [this.raw.uid]
      }
    ];
  }

  buildRoutes(): GaliaRoute[] {
    return [];
  }

  buildAreas(): GaliaArea[] {
    return [];
  }

  buildCalendarEvents(): CalendarEvent[] {
    const action = this.raw;
    const start = getFirstServiceStartOrScheduledFirstStepStart(action);
    const end = getLastServiceFinish(action) ?? start;

    const event = {
      uid: this.raw.uid,
      title: this.raw.uid,
      start,
      end,
      color: this.color,
      labels: []
    };

    return [event];
  }

  getDetailsCfg(): EntityCfg<unknown> | undefined {
    return undefined;
  }

  getDetails(): EntityInterface<unknown>[] {
    return [];
  }
}

export function registerRouteActionExtensions(
  registry: GenericEntityRegistryService
): void {
  // UserRouteAction columns are heavily computed (durations summed per step kind,
  // compartment-aware load/unload aggregation) and cannot be derived from the schema,
  // so the override fully replaces the auto-generated columns with the curated set.
  // The same UserRouteActionCfg also backs the route_action detail panel (routeDetailHandlers).
  const cfg = new UserRouteActionCfg();
  const overrides: EntityCfgOverrides<UserRouteActionV1Model> = {
    defaultSortingCol: cfg.defaultSortingCol(),
    customVisualizations: cfg.supportedVisualizations(),
    // The deployed schema does not declare the occupation module yet.
    extraVisualizations: new Set<VisualizationModule>(['occupation']),
    // Nor the site behind each stopover, which is the only source of marker coordinates,
    // nor its route, whose stopover count is what identifies the finish.
    extraAuxEntities: [
      { type: 'site', versionName: 'UserSite', uidPaths: ['site_uid'] },
      { type: 'route', versionName: 'UserRoute', uidPaths: ['route_uid'] }
    ],
    splitColumnTargetVersionNames: ['UserCapacityResource'],
    buildColumns: (_autoColumns, getSplitEntities) => cfg.buildColumns(getSplitEntities)
  };
  registry.registerOverrides('UserRouteAction', overrides);

  const builders: VisualizationBuilders<UserRouteActionV1Model> = {
    buildMarkers: (
      raw: UserRouteActionV1Model,
      auxData?: AuxiliaryDataBundle
    ): GaliaMarker[] => {
      const auxiliarySites: Record<string, UserSiteV1Model> = Object.fromEntries(
        ((auxData?.entitiesArrayPerVersionName['UserSite'] ?? []) as UserSiteV1Model[]).map(
          (site) => [site.uid, site]
        )
      );
      const coords = auxiliarySites[raw.site_uid]?.coords;

      if (isNil(coords)) {
        warnUnresolvedStopoverSites(raw.uid, [raw.site_uid]);
        return [];
      }

      const color = colorOfRoute(raw.route_uid, auxData);
      const labels = createActionLabels(raw, raw.uid);
      // Only the route knows how many stopovers it has, and that is what makes the last one
      // recognisable as the finish.
      const actionCount = (
        (auxData?.entitiesArrayPerVersionName['UserRoute'] ?? []) as {
          uid: string;
          actions?: unknown[];
        }[]
      ).find((route) => route.uid === raw.route_uid)?.actions?.length;

      return [
        {
          uid: raw.uid,
          title: raw.uid,
          coords,
          color,
          type: getStopoverMarkerType(raw, actionCount),
          description: {
            name: raw.uid,
            startDate: getFirstServiceStartOrScheduledFirstStepStart(raw),
            endDate: getLastServiceFinish(raw),
            content: labels
          },
          structuralAggregatorIds: [raw.uid]
        }
      ];
    },
    buildRoutes: (): [] => [],
    buildCalendarEvents: (
      raw: UserRouteActionV1Model,
      auxData?: AuxiliaryDataBundle
    ): CalendarEvent[] => {
      const color = colorOfRoute(raw.route_uid, auxData);
      const start = getFirstServiceStartOrScheduledFirstStepStart(raw);
      const end = getLastServiceFinish(raw) ?? start;
      return [
        {
          uid: raw.uid,
          title: raw.uid,
          start,
          end,
          mainColor: color,
          labels: []
        }
      ];
    },
    occupationSiteUids: (raw: UserRouteActionV1Model): string[] => [raw.site_uid]
  };
  registry.registerVisualizationBuilders('UserRouteAction', builders);
}
